import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import CreateReportingFramework from "@/components/CreateReportingFramework";   // Step 12 add-on G3
import { asArray } from "@/lib/asArray";
import React, { useEffect, useState } from "react";
import { clientName, engagementName, lockLabels } from "@/lib/engagementLabel";   // Batch 8.4 · A3 (F-54); Batch 14 · M13 / M14
import { fmtDate } from "@/lib/dt";
import DateInput from "@/components/DateInput";
import { useSearchParams } from "react-router-dom";
import { useEngagement } from "@/contexts/EngagementContext";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { useReviewRoles } from "@/lib/reviewRoles";   // STEP 3 · Rule 8: the server's role sets
import api, { toErrMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Lock, Trash2, Users, Settings2, Copy, CalendarPlus, ListChecks, Pencil, Package } from "lucide-react";
import { toast } from "sonner";
// Stage 2 "Dashboard" · 2.2 (C-14): SignOffCard moved to File Assembly, the engagement AttachmentPanel to Documents
import { useEngagementFigures, StatusCell, SignoffsCell, ReadyCell, ReportCell } from "@/components/EngagementFigureCells";   // 2.2
import { useStageFilter, StageFilterBar } from "@/components/EngagementStageFilter";   // PR 58 / 59 · F6: ?stage= from the dashboard
import EngagementTeamDialog from "@/components/EngagementTeamDialog";
import EngagementSettingsDialog from "@/components/EngagementSettingsDialog";
import CopyEngagementDialog from "@/components/CopyEngagementDialog";   // Batch 10 · Stage A
import RollForwardDialog from "@/components/RollForwardDialog";   // Batch 10.1 · R2
import EngagementPackageDialog, { RestorePackageButton } from "@/components/EngagementPackageDialog";   // Batch 14 · M15
import ReviewLevelsDialog from "@/components/ReviewLevelsDialog";   // Batch 11 · A1
import { askPrompt } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)
import EngagementLocaleFields, { localeValueOf, localeErrors, localePayload } from "@/components/EngagementLocaleFields";   // PR 45 review · D2

export default function Engagements() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const { engagements, refresh, setCurrentEngId } = useEngagement();
  const figures = useEngagementFigures(engagements);   // 2.2: one GET /engagements/{id}/figures per row
  const stageFilter = useStageFilter(engagements, figures);   // F6: rows = all, or the loaded rows of ?stage=
  const engCols = useResizableColumns({ storageKey: "engagements", defaults: [210, 92, 104, 115, 104, 92, 115, 138, 184], mins: [140, 70, 80, 90, 80, 70, 80, 96, 140] });   // client, ref, FY, type, status, sign-offs, ready to lock, report, actions (were 18/8/9/10/9/8/10/12/16 %)
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    client_name: "", financial_year: String(new Date().getFullYear()),
    // PR 45 review · D2: no "AED" default — country / currency / weekend below. Step 3 · rule 3 (B-3e): no materiality default
    // either (was 25,000 / 18,750 typed in as constants): empty = "not set" until typed here or set on 105(a)
    overall_materiality: "", performance_materiality: "",
    firm_engagement_ref: "", period_end: "", period_start: "", status: "Planning", requires_eqc: false,
    contracted_fee: 0, budgeted_hours: 0,
    // R19-R21: mandatory; no default. Client hard-block prevents Create
    // until user picks IAS 1 or IFRS 18. Server also enforces (belt + braces).
    presentation_framework: "",
    reporting_framework: "",          // Step 12 add-on G3: optional ("" = not chosen: today's default)
    // Batch 0 Part 1.1 — required. Client hard-blocks Create until picked;
    // server rejects unknown / coming-soon values (belt + braces).
    engagement_type: "",
    // Batch C · Round C1 · Q3 — Initial / Continuing engagement flag.
    // Empty = "Not set" (auditor must pick later on the Opening Balances
    // sheet or in Engagement Settings). No silent default.
    initial_or_continuing: "",
  });
  const [pfError, setPfError] = useState("");
  // PR 45 review · D2 (Card V3): country, currency and weekend — required, no default (the server answers 422 without them)
  const [locale, setLocale] = useState(() => localeValueOf(null));
  const [localeErr, setLocaleErr] = useState({});
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [teamEng, setTeamEng] = useState(null);
  const [settingsEng, setSettingsEng] = useState(null);
  const [copyEng, setCopyEng] = useState(null);   // Batch 10 · A1
  // Batch 14 · M13 (F-112): rename the engagement (any role on it, any stage — also Final / locked); logged old → new
  const rename = async (e) => {
    const v = await askPrompt(t("engagements.renamePrompt"), engagementName(e, t), { title: t("engagements.rename"), okLabel: t("common.save"), mustChange: true });   // Batch 15 · N4: Save only when changed and not blank
    if (v === null) return;
    try {
      await api.patch(`/engagements/${e.id}/name`, { engagement_name: v.trim() });
      toast.success(t("engagements.renamed"));
      await refresh();
    } catch (err) { toast.error(toErrMsg(err, t("common.failed"))); }
  };
  const [rfEng, setRfEng] = useState(null);   // Batch 10.1 · R2 — Roll forward to next year
  const [pkgEng, setPkgEng] = useState(null);   // Batch 14 · M15 — backup package
  const [rlEng, setRlEng] = useState(null);   // Batch 11 · A1 — Review levels
  // Round 24b · L3 — deep link from the Disclosure Checklist: /engagements?settings=<id>
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const sid = searchParams.get("settings");
    if (sid && !settingsEng) {
      const e = asArray(engagements).find((x) => x.id === sid);
      if (e) setSettingsEng(e);
    }
  }, [searchParams, engagements]); // eslint-disable-line react-hooks/exhaustive-deps
  const canDelete = user?.role === "Admin" || user?.role === "Partner";
  const canManageTeam = user?.role === "Admin" || user?.role === "Partner";
  // Batch 32 · T8 (owner 20-Oct-2026, "same access for every team level"): every team role manages the team of an
  // engagement it is on (the list shows only those, Admin / Partner see all); the dialog asks the server (team-rights)
  // STEP 3 · B-8b: every role check below reads the server's own sets (GET /review-roles → backend review_roles.py)
  const { can } = useReviewRoles();
  const canTeamDialog = can("engagement.team", user?.role);
  const canBillingFields = user?.role === "Admin" || user?.role === "Partner";
  // Batch 10 · A1: Partner and Manager of the same firm (any status, a locked engagement included); the server checks it again
  const canCopy = can("engagement.copy", user?.role);
  const canReviewLevels = can("signoff.setup", user?.role);

  const removeEngagement = async (id) => {
    setDeletingId(id);
    try {
      await api.delete(`/engagements/${id}`);
      toast.success(t("engagements.deleted"));
      await refresh();
    } catch (e) {
      toast.error(toErrMsg(e, t("engagements.deleteFailed")));
    } finally { setDeletingId(null); }
  };

  const create = async () => {
    // R19-R21 hard block — no engagement created until framework is chosen.
    if (!(form.client_name || "").trim()) { toast.error(t("engagements.clientNameRequired")); return; }   // Batch 8.4 · A3 (F-54); the server refuses too
    if (!form.presentation_framework) {
      setPfError(t("engagements.pfErrorSelect"));
      return;
    }
    // Batch 0 Part 1.1 — engagement type is required (must be selectable).
    if (!form.engagement_type) {
      toast.error(t("engagementType.required"));
      return;
    }
    const lerr = localeErrors(locale);
    setLocaleErr(lerr);
    if (Object.keys(lerr).length) { toast.error(t(`engLocale.${Object.values(lerr)[0]}`)); return; }
    setPfError("");
    setSaving(true);
    try {
      const payload = { ...form, ...localePayload(locale), overall_materiality: Number(form.overall_materiality || 0),
        performance_materiality: Number(form.performance_materiality || 0), materiality: Number(form.overall_materiality || 0),
        clearly_trivial: Number(form.clearly_trivial || 0) };
      const r = await api.post("/engagements", payload);
      toast.success(t("engagements.created"));
      await refresh();
      setCurrentEngId(r.data.id);
      setOpen(false);
      setLocale(localeValueOf(null)); setLocaleErr({});   // PR 45 review · D2: the next engagement starts with no country / currency / weekend
    } catch (e) {
      // PR 45 review · D2: the server's coded 422 names the field — mark it red on the form
      const f = e?.response?.data?.field;
      if (f) {
        const k = f === "weekend_days" ? "weekend" : f;
        setLocaleErr({ [k]: k === "currency" && /INVALID/.test(e?.response?.data?.error_code || "") ? "otherInvalid" : `${k}Required` });
      }
      toast.error(toErrMsg(e, t("engagements.failedCreate")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="engagements-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("engagements.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("engagements.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        {can("engagement.restore_package", user?.role) && <RestorePackageButton onRestored={(r) => { refresh(); if (r?.engagement_id) setCurrentEngId(r.engagement_id); }} />}   {/* Batch 14 · M15 */}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="new-eng-btn">
              <Plus className="me-1 h-4 w-4" /> {t("engagements.newEng")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("engagements.addTitle")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>{t("engagements.client")}</Label>
                <Input data-testid="eng-client" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
              </div>
              <div>
                <Label>{t("engagements.firmRef")}</Label>
                <Input data-testid="eng-firm-ref" placeholder={t("engagements.pfPlaceholder")}
                       value={form.firm_engagement_ref}
                       onChange={(e) => setForm({ ...form, firm_engagement_ref: e.target.value })} />
                <div className="mt-1 text-[11px] text-muted-foreground">{t("engagements.firmRefHint")}</div>
              </div>
              <EngagementLocaleFields value={locale} onChange={(v) => { setLocale(v); if (Object.keys(localeErr).length) setLocaleErr(localeErrors(v)); }}
                                      t={t} lang={lang} errors={localeErr} testIdPrefix="eng-locale" />
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>{t("engagements.fy")}</Label>
                  <Input data-testid="eng-fy" value={form.financial_year} onChange={(e) => setForm({ ...form, financial_year: e.target.value })} />
                </div>
                <div>
                  <Label>{t("engagements.periodEnd")}</Label>
                  <DateInput data-testid="eng-period-end" value={form.period_end}
                         onChange={(v) => setForm({ ...form, period_end: v })} />
                </div>
                <div>
                  <Label>{t("engagements.periodStart")}</Label>
                  <DateInput data-testid="eng-period-start" value={form.period_start}
                         onChange={(v) => setForm({ ...form, period_start: v })} />
                  <div className="mt-1 text-[11px] text-muted-foreground">{t("engagements.periodStartHint")}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("engagements.overallMateriality")}</Label>
                  <Input type="number" data-testid="eng-materiality" value={form.overall_materiality}
                         onChange={(e) => setForm({ ...form, overall_materiality: e.target.value === "" ? "" : Number(e.target.value) })} />
                </div>
                <div>
                  <Label>{t("engagements.performanceMateriality")}</Label>
                  <Input type="number" data-testid="eng-perf-materiality" value={form.performance_materiality}
                         onChange={(e) => setForm({ ...form, performance_materiality: e.target.value === "" ? "" : Number(e.target.value) })} />
                </div>
              </div>
              {/* Batch 27 · D (M37): optional; seeds 105(a). Empty = "Clearly trivial not set" until 105(a) sets it */}
              <div>
                <Label>{t("engagements.clearlyTrivial")}</Label>
                <Input type="number" min="0" data-testid="eng-clearly-trivial" value={form.clearly_trivial ?? ""}
                       onChange={(e) => setForm({ ...form, clearly_trivial: e.target.value === "" ? "" : Number(e.target.value) })} />
                <div className="mt-1 text-[11px] text-muted-foreground">{t("engagements.clearlyTrivialHint")}</div>
              </div>
              {canBillingFields && (
                <div className="grid grid-cols-2 gap-3 rounded-md border border-emerald-200 bg-emerald-50/40 p-3 dark:bg-emerald-950/20 dark:border-emerald-900">
                  <div>
                    <Label className="text-xs">{t("engagements.contractedFee")}</Label>
                    <Input type="number" min="0" data-testid="eng-contracted-fee"
                           value={form.contracted_fee}
                           onChange={(e) => setForm({ ...form, contracted_fee: Number(e.target.value) })} />
                    <div className="mt-1 text-[10px] text-muted-foreground">{t("engagements.contractedFeeHint")}</div>
                  </div>
                  <div>
                    <Label className="text-xs">{t("engagements.budgetedHours")}</Label>
                    <Input type="number" min="0" step="0.5" data-testid="eng-budgeted-hours"
                           value={form.budgeted_hours}
                           onChange={(e) => setForm({ ...form, budgeted_hours: Number(e.target.value) })} />
                    <div className="mt-1 text-[10px] text-muted-foreground">{t("engagements.budgetedHoursHint")}</div>
                  </div>
                </div>
              )}
              <p className="text-[11px] text-muted-foreground -mt-1">
                {t("engagements.seedNote").replace("{matPath}", t("engagements.seedNotePath"))}
              </p>
              {/* R19-R21 — mandatory Presentation Framework selector.
                   NO default option. Server rejects a blank value with a 400
                   even if the client is stale. */}
              <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3 dark:border-emerald-900 dark:bg-emerald-950/20">
                <Label className="text-xs font-semibold">
                  {t("engagements.pfLabel")} <span className="text-red-600">*</span>
                </Label>
                <div className="mt-2 flex gap-2" role="radiogroup" aria-label={t("engagements.pfLabel")}
                     data-testid="eng-pf-group">
                  {["IAS 1", "IFRS 18"].map((opt) => {
                    const active = form.presentation_framework === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => { setForm({ ...form, presentation_framework: opt }); setPfError(""); }}
                        className={`flex-1 rounded-md border px-3 py-1.5 text-sm transition ${
                          active
                            ? "border-emerald-500 bg-white font-semibold text-emerald-700 shadow-sm dark:bg-slate-900 dark:text-emerald-300"
                            : "border-emerald-200 bg-transparent text-slate-600 hover:bg-white dark:hover:bg-slate-900/60"
                        }`}
                        data-testid={`eng-pf-${opt.replace(/\s+/g, "").toLowerCase()}`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
                {pfError && (
                  <div className="mt-2 text-[11px] font-medium text-red-700 dark:text-red-300"
                       data-testid="eng-pf-error">
                    {pfError}
                  </div>
                )}
                <div className="mt-1 text-[10px] text-muted-foreground">
                  {t("engagements.pfNote")}
                </div>
              </div>
              <CreateReportingFramework value={form.reporting_framework}
                                        onChange={(v) => setForm({ ...form, reporting_framework: v })} />
              <label className="flex items-center gap-2 rounded-md border bg-slate-50 p-2 text-xs cursor-pointer dark:bg-slate-900/50">
                <input type="checkbox" checked={form.requires_eqc}
                       onChange={(e) => setForm({ ...form, requires_eqc: e.target.checked })}
                       data-testid="eng-requires-eqc" />
                <span><b>{t("engagements.requiresEqcLabel")}</b> {t("engagements.isaCitationEqc")}</span>
              </label>
              {/* Batch 0 Part 1.1 — Engagement type selector. 6 options; 2
                   selectable + 4 disabled with 'Coming soon' badge. Server
                   double-enforces (see engagement_types.py). */}
              <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3 dark:border-emerald-900 dark:bg-emerald-950/20">
                <Label className="text-xs font-semibold">
                  {t("engagementType.label")} <span className="text-red-600">*</span>
                </Label>
                <div className="mt-2 grid grid-cols-1 gap-1.5" role="radiogroup"
                     aria-label={t("engagementType.label")} data-testid="eng-type-group">
                  {[
                    { id: "audit", selectable: true },
                    { id: "review", selectable: true },
                    // Round 24c · M2 — coming-soon types hidden (flag only; code/data/forms kept).
                    { id: "interim_review", selectable: false, hidden: true },
                    { id: "special_purpose", selectable: false, hidden: true },
                    { id: "single_statement", selectable: false, hidden: true },
                    { id: "summary_fs", selectable: false, hidden: true },
                  ].filter((o) => !o.hidden).map((opt) => {
                    const active = form.engagement_type === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        disabled={!opt.selectable}
                        onClick={() => opt.selectable && setForm({ ...form, engagement_type: opt.id })}
                        className={`flex items-center justify-between rounded-md border px-3 py-1.5 text-xs transition ${
                          !opt.selectable
                            ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 dark:border-slate-800 dark:bg-slate-900/50 dark:text-slate-500"
                            : active
                              ? "border-emerald-500 bg-white font-semibold text-emerald-700 shadow-sm dark:bg-slate-900 dark:text-emerald-300"
                              : "border-emerald-200 bg-transparent text-slate-600 hover:bg-white dark:hover:bg-slate-900/60"
                        }`}
                        data-testid={`eng-type-${opt.id}`}
                      >
                        <span>{t(`engagementType.${opt.id}`)}</span>
                        {!opt.selectable && (
                          <span className="ms-2 rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 dark:bg-amber-950 dark:text-amber-200">
                            {t("engagementType.comingSoonBadge")}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* Batch C · Round C1 · Q3 — Initial / Continuing engagement.
                   Empty is allowed at create ("Not set"); auditor can pick
                   here or later on Opening Balances / Engagement Settings. */}
              <div className="rounded-md border border-blue-200 bg-blue-50/60 p-3 dark:border-blue-900 dark:bg-blue-950/20">
                <Label className="text-xs font-semibold">{t("eng.status.label")}</Label>
                <div className="mt-2 flex flex-wrap gap-1.5" role="radiogroup"
                       aria-label={t("eng.status.label")} data-testid="eng-status-group">
                  {[
                    { id: "", label: t("eng.status.notSet") },
                    { id: "initial", label: t("eng.status.initial") },
                    { id: "continuing", label: t("eng.status.continuing") },
                  ].map((opt) => {
                    const active = form.initial_or_continuing === opt.id;
                    return (
                      <button key={opt.id || "unset"} type="button" role="radio"
                                aria-checked={active}
                                onClick={() => setForm({ ...form, initial_or_continuing: opt.id })}
                                className={`rounded-md border px-3 py-1.5 text-xs transition ${active ? "border-blue-500 bg-white font-semibold text-blue-700 shadow-sm dark:bg-slate-900 dark:text-blue-300" : "border-blue-200 bg-transparent text-slate-600 hover:bg-white dark:hover:bg-slate-900/60"}`}
                                data-testid={`eng-status-${opt.id || "unset"}`}>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">{t("eng.status.hint")}</div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
              <Button onClick={create} disabled={saving} data-testid="eng-save-btn" className="bg-slate-900 hover:bg-slate-800">
                {t("common.create")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stage 2 "Dashboard" · 2.2 — 9 fixed-width columns (widths sum to 100 %, text wraps, no horizontal scroll at 1280 px).
          Status / Sign-offs / Ready to lock / Report come from GET /engagements/{id}/figures (one source each, Part C C-1…C-4). */}
      <StageFilterBar {...stageFilter} t={t} />
      <Card>
        <CardContent className="p-0">
          <ResizableTable cols={engCols} wrap className="w-full text-sm break-words" data-testid="eng-table">
            <thead className="border-b bg-slate-50 text-xs uppercase tracking-wider text-muted-foreground dark:bg-slate-900/60">
              <tr>
                <ResizableTh index={0} className="px-4 py-2 text-start">{t("engagements.client")}</ResizableTh>
                <ResizableTh index={1} className="px-4 py-2 text-start">{t("dsD.refCol")}</ResizableTh>
                <ResizableTh index={2} className="px-4 py-2 text-start">{t("dsD.fyCol")}</ResizableTh>
                <ResizableTh index={3} className="px-4 py-2 text-start">{t("engagementType.label")}</ResizableTh>
                <ResizableTh index={4} className="px-4 py-2 text-start">{t("common.status")}</ResizableTh>
                <ResizableTh index={5} className="px-4 py-2 text-start">{t("dsD.signoffsCol")}</ResizableTh>
                <ResizableTh index={6} className="px-4 py-2 text-start">{t("dsD.readyCol")}</ResizableTh>
                <ResizableTh index={7} className="px-4 py-2 text-start">{t("dsD.reportCol")}</ResizableTh>
                <ResizableTh index={8} className="px-4 py-2 text-end">{t("common.actions")}</ResizableTh>
              </tr>
            </thead>
            <tbody>
              {stageFilter.rows.map((e) => (
                <tr key={e.id} className="border-b hover:bg-slate-50 dark:hover:bg-slate-900/50" data-testid={`eng-row-${e.id}`}>
                  <td className="px-4 py-3 font-medium">
                    <div className="flex items-center gap-2">
                      {lockLabels(e, t, fmtDate).length > 0 && <Lock className="h-3 w-3 text-amber-600" data-testid={`eng-lock-${e.id}`} />}
                      <span data-testid={`eng-name-${e.id}`}>{engagementName(e, t)}</span>
                      <button type="button" className="text-slate-400 hover:text-slate-700" title={t("engagements.rename")} onClick={() => rename(e)} data-testid={`eng-rename-${e.id}`}><Pencil className="h-3 w-3" /></button>
                    </div>
                    {String(e.engagement_name || "").trim() && <div className="text-[11px] font-normal text-muted-foreground">{clientName(e, t)}</div>}
                    {lockLabels(e, t, fmtDate).length > 0 && <div className="text-[10px] font-normal text-amber-700" data-testid={`eng-lock-label-${e.id}`}>{lockLabels(e, t, fmtDate).join(" · ")}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {e.firm_engagement_ref ? (
                      <span className="rounded-md bg-emerald-50 px-2 py-0.5 font-mono text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                        {e.firm_engagement_ref}
                      </span>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3">{e.financial_year}</td>
                  <td className="px-4 py-3">
                    {e.engagement_type ? (
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                            data-testid={`eng-type-badge-${e.id}`}>
                        {t(`engagementType.${e.engagement_type}`)}
                      </span>
                    ) : (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                            data-testid={`eng-type-badge-${e.id}`}
                            title={t("engagementType.notSetHint")}>
                        {t("engagementType.notSet")}
                      </span>
                    )}
                  </td>
                  {/* 2.2: derived stage (C-1), sign-offs n / total (C-2), lock readiness (C-3), opinion + report date (C-4) — each a link */}
                  <td className="px-4 py-3"><StatusCell eng={e} fig={figures[e.id]} t={t} onOpen={setCurrentEngId} /></td>
                  <td className="px-4 py-3"><SignoffsCell eng={e} fig={figures[e.id]} t={t} onOpen={setCurrentEngId} /></td>
                  <td className="px-4 py-3"><ReadyCell eng={e} fig={figures[e.id]} t={t} onOpen={setCurrentEngId} /></td>
                  <td className="px-4 py-3"><ReportCell eng={e} fig={figures[e.id]} t={t} onOpen={setCurrentEngId} /></td>
                  <td className="px-4 py-3 text-end space-x-1">
                    <Button size="sm" variant="outline" data-testid={`eng-open-${e.id}`} onClick={() => setCurrentEngId(e.id)}>
                      {t("common.openEng")}
                    </Button>
                    <Button size="sm" variant="outline"
                            onClick={() => setSettingsEng(e)}
                            title={e.presentation_framework
                              ? t("engagements.pfWithLabel").replace("{pf}", e.presentation_framework)
                              : t("engagements.pfSetTooltip")}
                            data-testid={`eng-settings-${e.id}`}
                            className={!e.presentation_framework
                              ? "border-amber-400 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-800"
                              : ""}>
                      <Settings2 className="h-3.5 w-3.5" />
                    </Button>
                    {(canManageTeam || canTeamDialog) && (   /* Batch 32 · T8: every team level (the dialog asks the server) */
                      <Button size="sm" variant="outline" onClick={() => setTeamEng(e)}
                                title={t("engagements.manageTeam")}
                                data-testid={`eng-team-${e.id}`}>
                        <Users className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setPkgEng(e)} title={t("pkg.title")} data-testid={`eng-package-${e.id}`}>
                      <Package className="h-3.5 w-3.5" />
                    </Button>
                    {canCopy && (
                      <Button size="sm" variant="outline" onClick={() => setCopyEng(e)}
                                title={t("engagements.copyAction")}
                                data-testid={`eng-copy-${e.id}`}>
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {canReviewLevels && (
                      <Button size="sm" variant="outline" onClick={() => setRlEng(e)}
                                title={t("itemSignoff.reviewLevels")}
                                data-testid={`eng-review-levels-${e.id}`}>
                        <ListChecks className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {canCopy && (
                      <Button size="sm" variant="outline" onClick={() => setRfEng(e)}
                                title={t("engagements.rfAction")}
                                data-testid={`eng-rf-${e.id}`}>
                        <CalendarPlus className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {canDelete && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost"
                                  disabled={e.signed_off || deletingId === e.id}
                                  title={e.signed_off ? t("engagements.deleteEngUnlockFirst") : t("engagements.deleteEngTooltip")}
                                  data-testid={`eng-delete-${e.id}`}>
                            <Trash2 className="h-3.5 w-3.5 text-red-600" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{t("engagements.deleteEngTitle")}</AlertDialogTitle>
                            <AlertDialogDescription>
                              {t("engagements.deleteEngDesc").replace("{client}", clientName(e, t)).replace("{fy}", String(e.financial_year))}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                            <AlertDialogAction onClick={() => removeEngagement(e.id)}
                                                 className="bg-red-600 hover:bg-red-700"
                                                 data-testid={`eng-delete-confirm-${e.id}`}>
                              {t("engagements.deleteEngYes")}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </ResizableTable>
        </CardContent>
      </Card>
      <EngagementTeamDialog eng={teamEng} open={!!teamEng}
                              onOpenChange={(v) => !v && setTeamEng(null)} />
      <CopyEngagementDialog eng={copyEng} open={!!copyEng}
                              onCopied={() => refresh()}
                              onOpenChange={(v, result) => { if (!v) { setCopyEng(null); if (result?.new_engagement_id) setCurrentEngId(result.new_engagement_id); } }} />
      <EngagementPackageDialog eng={pkgEng} open={!!pkgEng} onOpenChange={(v) => { if (!v) setPkgEng(null); }} />
      <RollForwardDialog eng={rfEng} open={!!rfEng}
                         onDone={() => refresh()}
                         onOpenChange={(v, result) => { if (!v) { setRfEng(null); if (result?.new_engagement_id) setCurrentEngId(result.new_engagement_id); } }} />
      <ReviewLevelsDialog eng={rlEng} open={!!rlEng} onOpenChange={(v) => { if (!v) { setRlEng(null); refresh(); } }} />
      <EngagementSettingsDialog engagement={settingsEng}
                                  open={!!settingsEng}
                                  onOpenChange={(v) => !v && setSettingsEng(null)}
                                  onSaved={refresh} />
    </div>
  );
}
